/**
 * 时区功能灰度判定（精简版）
 *
 * 仅涉及 1 个 config 项：
 *   timeZone_options → { open: boolean, key: [流程白名单] }
 *
 * 灰度策略：
 *   ① 总开关：timeZone_options.open !== true → 关闭
 *   ② 有流程标识的页面（工单详情）：
 *      当前 processDefinitionKey 必须在 timeZone_options.key 白名单内
 *   ③ 无流程标识的页面（待办/已办列表）：
 *      只要系统时区非东八区（Asia/Shanghai）即启用
 *
 * 使用示例：
 *   import { isTimezoneEnabled, isNonShanghaiTimezone } from '@/utils/timezoneGate';
 *   computed: {
 *     isTimezoneEnabled() {
 *       return isTimezoneEnabled({
 *         getConfigItem: this.getConfigItem,
 *         processDefinitionKey: this.ticketData?.ProcessDefinitionKey,
 *       });
 *     },
 *   }
 *
 *   // 无流程标识页面（列表页）：
 *   isTimezoneEnabled({
 *     getConfigItem: this.getConfigItem,
 *     skipProcessCheck: true,
 *   });
 */

// ============================================================
// 内部工具函数
// ============================================================

/**
 * 安全解析 config 值：兼容 string / object / null
 */
function safeParseConfig(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.error('[timezoneGate] 配置 JSON 解析失败:', err, raw);
      return null;
    }
  }
  return null;
}

/**
 * 判断系统时区是否为非东八区（Asia/Shanghai）
 * @returns {boolean}
 */
export function isNonShanghaiTimezone() {
  try {
    if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return false;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return !!tz && tz !== 'Asia/Shanghai';
  } catch (e) {
    return false;
  }
}

/**
 * 获取当前系统时区标识
 * @returns {string}
 */
export function getSystemTimezone() {
  try {
    if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return 'Asia/Shanghai';
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'Asia/Shanghai';
  } catch (e) {
    return 'Asia/Shanghai';
  }
}

/**
 * 判断当前流程是否在时区白名单内
 */
function isProcessAllowed(options, processDefinitionKey) {
  if (!options) return false;
  if (!processDefinitionKey) return false;
  if (!options.key || !Array.isArray(options.key) || options.key.length === 0) return false;
  return options.key.includes(processDefinitionKey);
}

// ============================================================
// 对外接口
// ============================================================

/**
 * 判断当前上下文是否应启用时区转换功能
 * @param {object} ctx
 * @param {Function} ctx.getConfigItem - 配置读取函数
 * @param {string}   [ctx.processDefinitionKey] - 当前流程标识（详情页必传）
 * @param {boolean}  [ctx.skipProcessCheck=false] - 跳过流程白名单（列表页使用）
 * @returns {boolean}
 */
export function isTimezoneEnabled(ctx = {}) {
  const {
    getConfigItem,
    processDefinitionKey,
    skipProcessCheck = false,
  } = ctx;

  if (typeof getConfigItem !== 'function') {
    console.warn('[timezoneGate] 缺少 getConfigItem，判定降级为 false');
    return false;
  }

  try {
    // ① 总开关
    const options = safeParseConfig(getConfigItem('timeZone_options'));
    if (!options || options.open !== true) return false;

    // ② 列表页（无流程标识）：只要非东八区即启用
    if (skipProcessCheck) {
      return isNonShanghaiTimezone();
    }

    // ③ 详情页：流程白名单
    if (!isProcessAllowed(options, processDefinitionKey)) return false;

    // 流程在白名单内 + 非东八区 → 启用
    return isNonShanghaiTimezone();
  } catch (err) {
    console.error('[timezoneGate] 灰度判定异常:', err);
    return false;
  }
}

export default isTimezoneEnabled;
